import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FolderOpen } from 'lucide-react';
import { RestoreProgressModal } from './RestoreProgressModal';
import { RestorePreviewModal } from './RestorePreviewModal';
import { RestoreMode } from '../hooks/useRestoreJobPolling';
import { FolderBrowserModal } from '@features/backups/components/FolderBrowserModal';

interface RestoreModalProps {
  isOpen: boolean;
  onClose: () => void;
  backupJobId: string;
  backupConfigName: string;
  backupType?: 'FILES' | 'POSTGRESQL';
  sourceDatabase?: string;
  sourceHost?: string;
  sourcePort?: number;
  sourceUsername?: string;
  agentId?: string;
  onSuccess?: () => void;
  selectedFiles?: string[];
}

interface PostgreSQLConfig {
  host: string;
  port: number;
  database: string;
  username: string;
  password: string;
  useBackupPassword: boolean;
}

interface CreateRestoreRequest {
  backup_job_id: string;
  restore_location: 'original' | 'custom';
  custom_path?: string;
  overwrite: boolean;
  verify?: boolean;
  selected_files?: string[];
  restore_mode?: RestoreMode;
  postgresql?: {
    host: string;
    port: number;
    database: string;
    username: string;
    password: string;
  };
}

export const RestoreModal = ({
  isOpen,
  onClose,
  backupJobId,
  backupConfigName,
  backupType = 'FILES',
  sourceDatabase = '',
  sourceHost = '',
  sourcePort = 5432,
  sourceUsername = '',
  agentId,
  onSuccess,
  selectedFiles,
}: RestoreModalProps) => {
  const { t } = useTranslation('errors');
  const [restoreLocation, setRestoreLocation] = useState<'original' | 'custom'>('original');
  const [customPath, setCustomPath] = useState('');
  const [overwrite, setOverwrite] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [createdRestoreJobId, setCreatedRestoreJobId] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showFolderBrowser, setShowFolderBrowser] = useState(false);

  // Restore mode state (only applicable for PostgreSQL)
  const [restoreMode, setRestoreMode] = useState<RestoreMode>('file');

  // Track if any PG field was modified from original values
  const [_pgFieldsModified, setPgFieldsModified] = useState(false);

  // PostgreSQL connection config - pre-populated from backup source
  const [pgConfig, setPgConfig] = useState<PostgreSQLConfig>({
    host: sourceHost || '',
    port: sourcePort || 5432,
    database: sourceDatabase || '',
    username: sourceUsername || '',
    password: '',
    useBackupPassword: true,
  });

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setRestoreLocation('original');
      setCustomPath('');
      setOverwrite(false);
      setError('');
      setCreatedRestoreJobId(null);
      setShowPreview(false);
      setRestoreMode('file');
      setPgFieldsModified(false);
      setPgConfig({
        host: sourceHost || '',
        port: sourcePort || 5432,
        database: sourceDatabase || '',
        username: sourceUsername || '',
        password: '',
        useBackupPassword: true,
      });
    }
  }, [isOpen, sourceDatabase, sourceHost, sourcePort, sourceUsername]);

  if (!isOpen) return null;

  const isPostgreSQL = backupType === 'POSTGRESQL';
  const isDirectMode = restoreMode === 'direct';
  const isFileMode = restoreMode === 'file';
  const isOriginalServer = restoreLocation === 'original';
  const isSeparateServer = restoreLocation === 'custom';

  // Simple validation steps for the Start Restore button
  const canStartRestore = (): boolean => {
    // Step 1: Not loading
    if (loading) return false;

    // Step 2: File mode needs a path
    if (isFileMode) {
      return customPath.trim().length > 0;
    }

    // Step 3: Direct mode needs PostgreSQL config
    if (isDirectMode) {
      // Required: host, database, username
      if (!pgConfig.host || !pgConfig.database || !pgConfig.username) {
        return false;
      }
      // Separate server needs password
      if (isSeparateServer && !pgConfig.password) {
        return false;
      }
      // Original server needs overwrite confirmation
      if (isOriginalServer && !overwrite) {
        return false;
      }
      return true;
    }

    // Step 4: Regular file restore needs location
    if (restoreLocation === 'custom' && !customPath) {
      return false;
    }

    // Step 5: Original location needs overwrite confirmation
    if (restoreLocation === 'original' && !overwrite) {
      return false;
    }

    return true;
  };

  const handleRestore = async () => {
    setError('');
    setLoading(true);

    // Show warning if restoring to original location and not confirmed
    // Note: File mode doesn't need overwrite confirmation as it creates a new file
    if (restoreLocation === 'original' && !overwrite && !isDirectMode && !isFileMode) {
      setError(t('errors.confirmOverwrite'));
      setLoading(false);
      return;
    }

    // Validate PostgreSQL config for direct mode
    if (isDirectMode) {
      if (!pgConfig.host || !pgConfig.database || !pgConfig.username) {
        setError(t('errors.fillRequiredFields'));
        setLoading(false);
        return;
      }

      // Require password for separate server
      if (isSeparateServer && !pgConfig.password) {
        setError(t('errors.passwordRequired'));
        setLoading(false);
        return;
      }
    }

    const request: CreateRestoreRequest = {
      backup_job_id: backupJobId,
      restore_location: isFileMode ? 'custom' : restoreLocation,
      overwrite,
      selected_files: selectedFiles,
    };

    // Always use custom path for file mode
    if (isFileMode) {
      request.custom_path = customPath;
    } else if (isSeparateServer && customPath) {
      request.custom_path = customPath;
    }

    // Add restore mode for PostgreSQL backups
    if (isPostgreSQL) {
      request.restore_mode = restoreMode;

      // Add PostgreSQL config for direct mode
      if (isDirectMode) {
        request.postgresql = {
          host: pgConfig.host,
          port: pgConfig.port,
          database: pgConfig.database,
          username: pgConfig.username,
          password: pgConfig.useBackupPassword ? '' : pgConfig.password,
        };
      }
    }

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch('/api/v1/restore/jobs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || t('errors.createJobFailed'));
      }

      const data = await response.json();
      setCreatedRestoreJobId(data.id);
      if (onSuccess) onSuccess();
      setLoading(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.startRestoreFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Handle PG config changes with tracking
  const handlePgConfigChange = (
    field: keyof PostgreSQLConfig,
    value: string | number | boolean
  ) => {
    setPgConfig((prev) => ({ ...prev, [field]: value }));

    // Check if this is a meaningful change from original values
    if (field !== 'password' && field !== 'useBackupPassword') {
      const originalValue = {
        host: sourceHost,
        port: sourcePort,
        database: sourceDatabase,
        username: sourceUsername,
      }[field];

      if (value !== originalValue) {
        setPgFieldsModified(true);
      }
    }
  };

  // Handle server location change
  const handleServerLocationChange = (location: 'original' | 'custom') => {
    setRestoreLocation(location);

    if (location === 'original') {
      // Reset to original values
      setPgConfig({
        host: sourceHost || '',
        port: sourcePort || 5432,
        database: sourceDatabase || '',
        username: sourceUsername || '',
        password: '',
        useBackupPassword: true,
      });
      setPgFieldsModified(false);
    } else {
      // Clear all fields for separate server
      setPgConfig({
        host: '',
        port: 5432,
        database: '',
        username: '',
        password: '',
        useBackupPassword: false,
      });
      setPgFieldsModified(true);
    }
  };

  // Get restore mode description
  const getRestoreModeDescription = (mode: RestoreMode) => {
    switch (mode) {
      case 'file':
        return t('restoreMode.fileDescription');
      case 'direct':
        return t('restoreMode.directDescription');
      default:
        return '';
    }
  };

  return (
    <>
      {/* Progress Modal */}
      <RestoreProgressModal
        isOpen={createdRestoreJobId !== null}
        onClose={() => {
          setCreatedRestoreJobId(null);
          onClose();
        }}
        restoreJobId={createdRestoreJobId}
      />

      {/* Preview Modal */}
      <RestorePreviewModal
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        backupJobId={backupJobId}
        backupConfigName={backupConfigName}
        restoreLocation={restoreLocation}
        customPath={customPath}
        onConfirmRestore={handleRestore}
      />

      {/* Restore Modal */}
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 animate-in fade-in duration-300">
        <div className="bg-surface-card-light dark:bg-surface-card-dark rounded-2xl shadow-elevated max-w-2xl w-full mx-4 border border-surface-border-light dark:border-surface-border-dark overflow-hidden max-h-[90vh] flex flex-col">
          <div className="p-6 border-b border-surface-border-light dark:border-surface-border-dark">
            <h2 className="text-xl font-black text-slate-900 dark:text-white tracking-tight uppercase tracking-widest">
              {selectedFiles && selectedFiles.length > 0 ? t('selectiveTitle') : t('title')}
            </h2>
            <p className="text-sm text-text-muted-light dark:text-text-muted-dark mt-1 font-medium">
              {t('source')}:{' '}
              <span className="font-bold text-slate-700 dark:text-slate-200">
                {backupConfigName}
              </span>
              {isPostgreSQL && (
                <span className="ml-2 text-xs bg-state-info-bg-light dark:bg-state-info-bg-dark text-state-info-light dark:text-state-info-dark px-2 py-0.5 rounded-full">
                  PostgreSQL
                </span>
              )}
              {selectedFiles && selectedFiles.length > 0 && (
                <span className="ml-2 text-brand-600 dark:text-brand-400 font-bold">
                  ({selectedFiles.length} {selectedFiles.length !== 1 ? t('items') : t('item')}{' '}
                  {t('targeted')})
                </span>
              )}
            </p>
          </div>

          <div className="p-6 space-y-6 overflow-y-auto">
            {/* Error message */}
            {error && (
              <div className="bg-state-danger-bg-light dark:bg-state-danger-bg-dark border border-state-danger-light/20 dark:border-state-danger-dark/20 rounded-xl p-4">
                <p className="text-sm font-bold text-state-danger-light dark:text-state-danger-dark">
                  {error}
                </p>
              </div>
            )}

            {/* Restore Mode Selection - Only for PostgreSQL */}
            {isPostgreSQL && (
              <div className="animate-in slide-in-from-top-2 duration-300">
                <label className="block text-[10px] font-black uppercase tracking-widest text-text-subtle-light dark:text-text-subtle-dark mb-3 ml-1">
                  {t('restoreMode.label')}
                </label>
                <div className="space-y-3">
                  <label
                    className={`flex items-start p-4 border rounded-xl cursor-pointer transition-all ${restoreMode === 'file' ? 'border-brand-600 dark:border-brand-400 bg-accent-soft-light dark:bg-accent-soft-dark' : 'border-surface-border-light dark:border-surface-border-dark hover:bg-surface-2-light dark:hover:bg-surface-2-dark'}`}
                  >
                    <input
                      type="radio"
                      name="restoreMode"
                      value="file"
                      checked={restoreMode === 'file'}
                      onChange={(e) => setRestoreMode(e.target.value as RestoreMode)}
                      className="w-4 h-4 text-brand-600 dark:text-brand-400 focus:ring-brand-500 border-border-strong-light dark:border-border-strong-dark mt-0.5"
                    />
                    <div className="ml-4">
                      <div className="font-bold text-slate-900 dark:text-white">
                        {t('restoreMode.file')}
                      </div>
                      <div className="text-xs text-text-muted-light dark:text-text-muted-dark">
                        {getRestoreModeDescription('file')}
                      </div>
                    </div>
                  </label>
                  <label
                    className={`flex items-start p-4 border rounded-xl cursor-pointer transition-all ${restoreMode === 'direct' ? 'border-brand-600 dark:border-brand-400 bg-accent-soft-light dark:bg-accent-soft-dark' : 'border-surface-border-light dark:border-surface-border-dark hover:bg-surface-2-light dark:hover:bg-surface-2-dark'}`}
                  >
                    <input
                      type="radio"
                      name="restoreMode"
                      value="direct"
                      checked={restoreMode === 'direct'}
                      onChange={(e) => setRestoreMode(e.target.value as RestoreMode)}
                      className="w-4 h-4 text-brand-600 dark:text-brand-400 focus:ring-brand-500 border-border-strong-light dark:border-border-strong-dark mt-0.5"
                    />
                    <div className="ml-4">
                      <div className="font-bold text-slate-900 dark:text-white">
                        {t('restoreMode.direct')}
                      </div>
                      <div className="text-xs text-text-muted-light dark:text-text-muted-dark">
                        {getRestoreModeDescription('direct')}
                      </div>
                    </div>
                  </label>
                </div>
              </div>
            )}

            {/* File Mode - Custom Path Only */}
            {isPostgreSQL && isFileMode && (
              <div className="animate-in slide-in-from-top-2 duration-300">
                <label
                  htmlFor="customPath"
                  className="block text-[10px] font-black uppercase tracking-widest text-text-subtle-light dark:text-text-subtle-dark mb-2 ml-1"
                >
                  {t('customPathLabel')}
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    id="customPath"
                    value={customPath}
                    onChange={(e) => setCustomPath(e.target.value)}
                    placeholder={t('customPathPlaceholder')}
                    className="flex-1 px-4 py-3 bg-surface-2-light dark:bg-surface-card-dark border border-surface-border-light dark:border-surface-border-dark rounded-xl text-slate-900 dark:text-white font-bold focus:ring-2 focus:ring-brand-500 outline-none transition-all"
                  />
                  {agentId && (
                    <button
                      type="button"
                      onClick={() => setShowFolderBrowser(true)}
                      className="px-4 py-3 bg-surface-2-light dark:bg-surface-2-dark border border-surface-border-light dark:border-surface-border-dark rounded-xl text-slate-700 dark:text-slate-300 hover:bg-surface-border-light dark:hover:bg-surface-border-dark transition-colors font-medium flex items-center gap-2"
                      title={t('restoreModal.browseFoldersTitle')}
                      aria-label={t('restoreModal.browse')}
                    >
                      <FolderOpen className="w-5 h-5" />
                      <span className="hidden sm:inline">{t('restoreModal.browse')}</span>
                    </button>
                  )}
                </div>
                <p className="text-xs text-text-muted-light dark:text-text-muted-dark mt-2">
                  {t('customPathHint')}
                </p>
              </div>
            )}

            {/* PostgreSQL Server Selection - Only for Direct Mode */}
            {isDirectMode && (
              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-text-subtle-light dark:text-text-subtle-dark mb-3 ml-1">
                  {t('restoreModal.postgresqlServer')}
                </label>
                <div className="space-y-3">
                  <label
                    className={`flex items-center p-4 border rounded-xl cursor-pointer transition-all ${isOriginalServer ? 'border-brand-600 dark:border-brand-400 bg-accent-soft-light dark:bg-accent-soft-dark' : 'border-surface-border-light dark:border-surface-border-dark hover:bg-surface-2-light dark:hover:bg-surface-2-dark'}`}
                  >
                    <input
                      type="radio"
                      name="serverLocation"
                      value="original"
                      checked={isOriginalServer}
                      onChange={(e) =>
                        handleServerLocationChange(e.target.value as 'original' | 'custom')
                      }
                      className="w-4 h-4 text-brand-600 dark:text-brand-400 focus:ring-brand-500 border-border-strong-light dark:border-border-strong-dark"
                    />
                    <div className="ml-4">
                      <div className="font-bold text-slate-900 dark:text-white">
                        {t('restoreModal.originalDatabaseServer')}
                      </div>
                      <div className="text-xs text-text-muted-light dark:text-text-muted-dark">
                        {t('restoreModal.originalDatabaseServerDescription')}
                      </div>
                    </div>
                  </label>
                  <label
                    className={`flex items-center p-4 border rounded-xl cursor-pointer transition-all ${isSeparateServer ? 'border-brand-600 dark:border-brand-400 bg-accent-soft-light dark:bg-accent-soft-dark' : 'border-surface-border-light dark:border-surface-border-dark hover:bg-surface-2-light dark:hover:bg-surface-2-dark'}`}
                  >
                    <input
                      type="radio"
                      name="serverLocation"
                      value="custom"
                      checked={isSeparateServer}
                      onChange={(e) =>
                        handleServerLocationChange(e.target.value as 'original' | 'custom')
                      }
                      className="w-4 h-4 text-brand-600 dark:text-brand-400 focus:ring-brand-500 border-border-strong-light dark:border-border-strong-dark"
                    />
                    <div className="ml-4">
                      <div className="font-bold text-slate-900 dark:text-white">
                        {t('restoreModal.separateServer')}
                      </div>
                      <div className="text-xs text-text-muted-light dark:text-text-muted-dark">
                        {t('restoreModal.separateServerDescription')}
                      </div>
                    </div>
                  </label>
                </div>

                {/* Overwrite Warning for Original Server */}
                {isOriginalServer && (
                  <div className="mt-4 bg-state-warn-bg-light dark:bg-state-warn-bg-dark border border-state-warn-light/20 dark:border-state-warn-dark/20 rounded-xl p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg
                          className="h-5 w-5 text-state-warn-light dark:text-state-warn-dark"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-xs font-black uppercase tracking-widest text-state-warn-light dark:text-state-warn-dark">
                          {t('restoreModal.databaseOverwriteWarningTitle')}
                        </h3>
                        <div className="mt-1 text-sm text-state-warn-light dark:text-state-warn-dark font-medium">
                          <p>{t('restoreModal.databaseOverwriteWarning')}</p>
                        </div>
                      </div>
                    </div>
                    <div className="mt-4">
                      <label className="flex items-center group cursor-pointer">
                        <input
                          type="checkbox"
                          checked={overwrite}
                          onChange={(e) => setOverwrite(e.target.checked)}
                          className="w-4 h-4 text-state-warn-light dark:text-state-warn-dark focus:ring-state-warn-light dark:focus:ring-state-warn-dark border-state-warn-light dark:border-state-warn-dark rounded"
                        />
                        <span className="ml-3 text-xs text-state-warn-light dark:text-state-warn-dark font-black uppercase tracking-tighter group-hover:text-state-warn-light dark:group-hover:text-state-warn-dark transition-colors">
                          {t('restoreModal.confirmDatabaseOverwrite')}
                        </span>
                      </label>
                    </div>
                  </div>
                )}

                {/* PostgreSQL Connection Config - Below Server Selection */}
                <div className="mt-6 bg-state-info-bg-light dark:bg-state-info-bg-dark border border-state-info-light/20 dark:border-state-info-dark/20 rounded-xl p-4">
                  <label className="block text-[10px] font-black uppercase tracking-widest text-state-info-light dark:text-state-info-dark mb-4">
                    {t('restoreModal.postgresqlConnection')}
                    {isOriginalServer && (
                      <span className="ml-2 text-xs font-normal text-text-muted-light dark:text-text-muted-dark">
                        ({t('restoreModal.usingOriginalServer')})
                      </span>
                    )}
                    {isSeparateServer && (
                      <span className="ml-2 text-xs font-normal text-state-warn-light dark:text-state-warn-dark">
                        ({t('restoreModal.enterNewServerDetails')})
                      </span>
                    )}
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2 sm:col-span-1">
                      <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1.5">
                        {t('restoreModal.host')}{' '}
                        <span className="text-state-danger-light dark:text-state-danger-dark">
                          *
                        </span>
                      </label>
                      <input
                        type="text"
                        value={pgConfig.host}
                        onChange={(e) => handlePgConfigChange('host', e.target.value)}
                        placeholder={
                          isSeparateServer
                            ? t('restoreModal.enterHost')
                            : t('restoreModal.localHost')
                        }
                        disabled={isOriginalServer}
                        className="w-full px-3 py-2 bg-surface-card-light dark:bg-surface-card-dark border border-surface-border-light dark:border-surface-border-dark rounded-lg text-slate-900 dark:text-white font-medium focus:ring-2 focus:ring-brand-500 outline-none transition-all disabled:bg-surface-2-light dark:disabled:bg-surface-2-dark dark:disabled:bg-surface-2-dark disabled:text-text-muted-light dark:disabled:text-text-muted-dark disabled:cursor-not-allowed"
                      />
                    </div>
                    <div className="col-span-2 sm:col-span-1">
                      <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1.5">
                        {t('restoreModal.port')}
                      </label>
                      <input
                        type="number"
                        value={pgConfig.port}
                        onChange={(e) =>
                          handlePgConfigChange('port', parseInt(e.target.value) || 5432)
                        }
                        placeholder={t('restoreModal.portPlaceholder')}
                        disabled={isOriginalServer}
                        className="w-full px-3 py-2 bg-surface-card-light dark:bg-surface-card-dark border border-surface-border-light dark:border-surface-border-dark rounded-lg text-slate-900 dark:text-white font-medium focus:ring-2 focus:ring-brand-500 outline-none transition-all disabled:bg-surface-2-light dark:disabled:bg-surface-2-dark dark:disabled:bg-surface-2-dark disabled:text-text-muted-light dark:disabled:text-text-muted-dark disabled:cursor-not-allowed"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1.5">
                        {t('restoreModal.database')}{' '}
                        <span className="text-state-danger-light dark:text-state-danger-dark">
                          *
                        </span>
                      </label>
                      <input
                        type="text"
                        value={pgConfig.database}
                        onChange={(e) => handlePgConfigChange('database', e.target.value)}
                        placeholder={
                          isSeparateServer
                            ? t('restoreModal.enterDatabase')
                            : t('restoreModal.databasePlaceholder')
                        }
                        disabled={isOriginalServer}
                        className="w-full px-3 py-2 bg-surface-card-light dark:bg-surface-card-dark border border-surface-border-light dark:border-surface-border-dark rounded-lg text-slate-900 dark:text-white font-medium focus:ring-2 focus:ring-brand-500 outline-none transition-all disabled:bg-surface-2-light dark:disabled:bg-surface-2-dark dark:disabled:bg-surface-2-dark disabled:text-text-muted-light dark:disabled:text-text-muted-dark disabled:cursor-not-allowed"
                      />
                    </div>
                    <div className="col-span-2 sm:col-span-1">
                      <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1.5">
                        {t('restoreModal.username')}{' '}
                        <span className="text-state-danger-light dark:text-state-danger-dark">
                          *
                        </span>
                      </label>
                      <input
                        type="text"
                        value={pgConfig.username}
                        onChange={(e) => handlePgConfigChange('username', e.target.value)}
                        placeholder={
                          isSeparateServer
                            ? t('restoreModal.enterUsername')
                            : t('restoreModal.usernamePlaceholder')
                        }
                        disabled={isOriginalServer}
                        className="w-full px-3 py-2 bg-surface-card-light dark:bg-surface-card-dark border border-surface-border-light dark:border-surface-border-dark rounded-lg text-slate-900 dark:text-white font-medium focus:ring-2 focus:ring-brand-500 outline-none transition-all disabled:bg-surface-2-light dark:disabled:bg-surface-2-dark dark:disabled:bg-surface-2-dark disabled:text-text-muted-light dark:disabled:text-text-muted-dark disabled:cursor-not-allowed"
                      />
                    </div>
                    <div className="col-span-2 sm:col-span-1">
                      <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1.5">
                        {t('restoreModal.password')}
                        {pgConfig.useBackupPassword && isOriginalServer && (
                          <span className="ml-2 text-xs font-normal text-state-ok-light dark:text-state-ok-dark">
                            ({t('restoreModal.usingBackupPassword')})
                          </span>
                        )}
                      </label>
                      {isOriginalServer && pgConfig.useBackupPassword ? (
                        <button
                          type="button"
                          onClick={() => handlePgConfigChange('useBackupPassword', false)}
                          className="w-full px-3 py-2 bg-surface-2-light dark:bg-surface-2-dark border border-dashed border-surface-border-light dark:border-border-strong-dark rounded-lg text-slate-600 dark:text-text-subtle-dark text-sm font-medium hover:bg-surface-border-light dark:hover:bg-surface-border-dark transition-all text-left"
                        >
                          {t('restoreModal.clickToChangePassword')}
                        </button>
                      ) : (
                        <input
                          type="password"
                          value={pgConfig.password}
                          onChange={(e) => handlePgConfigChange('password', e.target.value)}
                          placeholder={
                            isSeparateServer
                              ? t('restoreModal.enterPassword')
                              : t('restoreModal.enterNewPassword')
                          }
                          className="w-full px-3 py-2 bg-surface-card-light dark:bg-surface-card-dark border border-surface-border-light dark:border-surface-border-dark rounded-lg text-slate-900 dark:text-white font-medium focus:ring-2 focus:ring-brand-500 outline-none transition-all"
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Target Destination - Only for non-PostgreSQL */}
            {!isPostgreSQL && (
              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-text-subtle-light dark:text-text-subtle-dark mb-3 ml-1">
                  {t('restoreModal.targetDestination')}
                </label>
                <div className="space-y-3">
                  <label
                    className={`flex items-center p-4 border rounded-xl cursor-pointer transition-all ${restoreLocation === 'original' ? 'border-brand-600 dark:border-brand-400 bg-accent-soft-light dark:bg-accent-soft-dark' : 'border-surface-border-light dark:border-surface-border-dark hover:bg-surface-2-light dark:hover:bg-surface-2-dark'}`}
                  >
                    <input
                      type="radio"
                      name="restoreLocation"
                      value="original"
                      checked={restoreLocation === 'original'}
                      onChange={(e) => setRestoreLocation(e.target.value as 'original' | 'custom')}
                      className="w-4 h-4 text-brand-600 dark:text-brand-400 focus:ring-brand-500 border-border-strong-light dark:border-border-strong-dark"
                    />
                    <div className="ml-4">
                      <div className="font-bold text-slate-900 dark:text-white">
                        {t('restoreModal.originalLocation')}
                      </div>
                      <div className="text-xs text-text-muted-light dark:text-text-muted-dark">
                        {t('restoreModal.originalLocationDescription')}
                      </div>
                    </div>
                  </label>
                  <label
                    className={`flex items-center p-4 border rounded-xl cursor-pointer transition-all ${restoreLocation === 'custom' ? 'border-brand-600 dark:border-brand-400 bg-accent-soft-light dark:bg-accent-soft-dark' : 'border-surface-border-light dark:border-surface-border-dark hover:bg-surface-2-light dark:hover:bg-surface-2-dark'}`}
                  >
                    <input
                      type="radio"
                      name="restoreLocation"
                      value="custom"
                      checked={restoreLocation === 'custom'}
                      onChange={(e) => setRestoreLocation(e.target.value as 'original' | 'custom')}
                      className="w-4 h-4 text-brand-600 dark:text-brand-400 focus:ring-brand-500 border-border-strong-light dark:border-border-strong-dark"
                    />
                    <div className="ml-4">
                      <div className="font-bold text-slate-900 dark:text-white">
                        {t('restoreModal.customLocation')}
                      </div>
                      <div className="text-xs text-text-muted-light dark:text-text-muted-dark">
                        {t('restoreModal.customLocationDescription')}
                      </div>
                    </div>
                  </label>
                </div>

                {/* Overwrite Warning for Original Location */}
                {restoreLocation === 'original' && (
                  <div className="mt-4 bg-state-warn-bg-light dark:bg-state-warn-bg-dark border border-state-warn-light/20 dark:border-state-warn-dark/20 rounded-xl p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg
                          className="h-5 w-5 text-state-warn-light dark:text-state-warn-dark"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-xs font-black uppercase tracking-widest text-state-warn-light dark:text-state-warn-dark">
                          {t('restoreModal.fileOverwriteWarningTitle')}
                        </h3>
                        <div className="mt-1 text-sm text-state-warn-light dark:text-state-warn-dark font-medium">
                          <p>{t('restoreModal.fileOverwriteWarning')}</p>
                        </div>
                      </div>
                    </div>
                    <div className="mt-4">
                      <label className="flex items-center group cursor-pointer">
                        <input
                          type="checkbox"
                          checked={overwrite}
                          onChange={(e) => setOverwrite(e.target.checked)}
                          className="w-4 h-4 text-state-warn-light dark:text-state-warn-dark focus:ring-state-warn-light dark:focus:ring-state-warn-dark border-state-warn-light dark:border-state-warn-dark rounded"
                        />
                        <span className="ml-3 text-xs text-state-warn-light dark:text-state-warn-dark font-black uppercase tracking-tighter group-hover:text-state-warn-light dark:group-hover:text-state-warn-dark transition-colors">
                          {t('restoreModal.confirmFileOverwrite')}
                        </span>
                      </label>
                    </div>
                  </div>
                )}

                {/* Custom Path */}
                {restoreLocation === 'custom' && (
                  <div className="mt-4 animate-in slide-in-from-top-2 duration-300">
                    <label
                      htmlFor="customPath"
                      className="block text-[10px] font-black uppercase tracking-widest text-text-subtle-light dark:text-text-subtle-dark mb-2 ml-1"
                    >
                      {t('restoreModal.customPathSpecification')}
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        id="customPath"
                        value={customPath}
                        onChange={(e) => setCustomPath(e.target.value)}
                        placeholder={t('restoreModal.customPathPlaceholder')}
                        className="flex-1 px-4 py-3 bg-surface-2-light dark:bg-surface-card-dark border border-surface-border-light dark:border-surface-border-dark rounded-xl text-slate-900 dark:text-white font-bold focus:ring-2 focus:ring-brand-500 outline-none transition-all"
                      />
                      {agentId && (
                        <button
                          type="button"
                          onClick={() => setShowFolderBrowser(true)}
                          className="px-4 py-3 bg-surface-2-light dark:bg-surface-2-dark border border-surface-border-light dark:border-surface-border-dark rounded-xl text-slate-700 dark:text-slate-300 hover:bg-surface-border-light dark:hover:bg-surface-border-dark transition-colors font-medium flex items-center gap-2"
                          title={t('restoreModal.browseFoldersTitle')}
                          aria-label={t('restoreModal.browse')}
                        >
                          <FolderOpen className="w-5 h-5" />
                          <span className="hidden sm:inline">{t('restoreModal.browse')}</span>
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="p-6 bg-surface-2-light dark:bg-surface-2-dark border-t border-surface-border-light dark:border-surface-border-dark flex justify-end gap-3">
            <button
              onClick={onClose}
              disabled={loading}
              className="px-6 py-2.5 text-xs font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark hover:text-slate-700 dark:hover:text-slate-300 transition-colors"
            >
              {t('restoreModal.cancel')}
            </button>
            {!isDirectMode && !isFileMode && (
              <button
                onClick={() => setShowPreview(true)}
                disabled={loading || (restoreLocation === 'custom' && !customPath)}
                className="px-6 py-2.5 bg-accent-soft-light dark:bg-accent-soft-dark text-brand-600 dark:text-brand-400 text-xs font-black uppercase tracking-widest rounded-xl hover:bg-accent-soft-light dark:hover:bg-accent-soft-dark transition-all disabled:opacity-50"
              >
                {t('restoreModal.preview')}
              </button>
            )}
            <button
              onClick={handleRestore}
              disabled={!canStartRestore()}
              className="px-6 py-2.5 bg-brand-600 text-white text-xs font-black uppercase tracking-widest rounded-xl shadow-lg shadow-brand-200 dark:shadow-none hover:bg-brand-700 transition-all active:scale-95 disabled:opacity-50"
            >
              {loading
                ? t('restoreModal.initializing')
                : isDirectMode
                  ? t('restoreModal.restoreToDatabase')
                  : isFileMode
                    ? t('restoreModal.restoreToFile')
                    : t('restoreModal.executeRestore')}
            </button>
          </div>
        </div>
      </div>

      {/* Folder Browser Modal */}
      {showFolderBrowser && agentId && (
        <FolderBrowserModal
          isOpen={showFolderBrowser}
          onClose={() => setShowFolderBrowser(false)}
          agentId={agentId}
          initialPath={customPath}
          selectedPaths={customPath ? [customPath] : []}
          onSelectPaths={(paths) => {
            if (paths.length > 0) {
              setCustomPath(paths[0]);
            }
          }}
          allowMultiple={false}
        />
      )}
    </>
  );
};
